import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { getAudit, listAudits, unwrapGet } from "../../../helpers/auditHub_helper";
import { APPROACHING_COMPLETION_DAYS, AUDIT_HUB_USE_MOCK, AUDIT_STATUSES } from "./constants";
import {
  requestAuditReport,
  resetAuditReport,
} from "../../../store/auditHub/auditHubSlice";
import { buildComplianceControlsPath, frameworkKey, readCurrentUserEmail } from "./utils";
import {
  AH_MUTED,
  AH_TEXT,
  AuditHubThemeProvider,
  auditHubPageSx,
  primaryButtonSx,
  tableShellSx,
  toolbarRowSx,
} from "./theme";
import SummaryCards from "./components/SummaryCards";
import FilterMenu from "./components/FilterMenu";
import {
  ActiveAuditsTable,
  CompletedAuditsTable,
  HubEmptyState,
} from "./components/AuditsTable";
import AuditComplianceDialog from "./components/AuditComplianceDialog";
import EditAuditDialog from "./components/EditAuditDialog";
import DeleteAuditDialog from "./components/DeleteAuditDialog";
import CompleteAuditDialog from "./components/CompleteAuditDialog";
import CancelAuditDialog from "./components/CancelAuditDialog";
import LaunchAuditDialog from "./components/LaunchAuditDialog";
import ActivityTrailDialog from "./components/ActivityTrailDialog";

const DETAILS_STATUSES = [AUDIT_STATUSES.DRAFT, AUDIT_STATUSES.ACTIVE];
const CLOSED_STATUSES = [AUDIT_STATUSES.COMPLETED, AUDIT_STATUSES.CANCELLED];

/** Header keys the list route can order. */
const LIST_SORT = {
  id: "auditCode",
  name: "name",
  owner: "ownerEmail",
  manual: "manualDecided",
  days: "dueDate",
  status: "status",
  completedOn: "lastActivityAt",
};

const nextSort = (current, field) =>
  current.orderBy === field
    ? { orderBy: field, order: current.order === "asc" ? "desc" : "asc" }
    : { orderBy: field, order: "asc" };

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const statusesFor = (selected, allowed) =>
  selected.length ? allowed.filter((status) => selected.includes(status)) : allowed;

const sortParams = (sortState) => {
  const sort = LIST_SORT[sortState?.orderBy];
  if (!sort) return {};
  return { sort, order: sortState.order === "desc" ? "desc" : "asc" };
};

/** Empty values stay last in both directions, matching the list route. */
const compareMissingLast = (left, right, direction) => {
  const leftMissing = left == null || left === "";
  const rightMissing = right == null || right === "";
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;
  if (typeof left === "number" && typeof right === "number") return (left - right) * direction;
  return String(left).localeCompare(String(right), undefined, { sensitivity: "base", numeric: true }) * direction;
};

const listSortValue = (item, sort) => {
  if (sort === "dueDate") return item?.dueDate || null;
  if (sort === "name") return item?.name || null;
  if (sort === "auditCode") return item?.auditCode || null;
  if (sort === "status") return item?.status || null;
  if (sort === "lastActivityAt") return item?.lastActivityAt || null;
  if (sort === "ownerEmail") return item?.ownerEmail || null;
  if (sort === "accountCount") return item?.accountCount ?? null;
  if (sort === "scopeCount") return item?.scopeCount ?? null;
  if (sort === "manualTotal") return item?.progress?.total ?? null;
  if (sort === "manualDecided") return item?.progress?.decided ?? null;
  if (sort === "manualUndecided") return item?.progress?.outstanding ?? null;
  return item?.createdAt || null;
};

/** The first `count` rows of one status, already ordered by the list route. */
const fetchStatusPrefix = async ({ status, q, sort, order, count }) => {
  const size = Math.min(100, Math.max(count, 1));
  const items = [];
  let total = 0;
  let page = 1;
  while (items.length < count) {
    const body = unwrapGet(await listAudits({ status, q, sort, order, page, size }));
    const chunk = Array.isArray(body?.items) ? body.items : [];
    total = Number(body?.total) || 0;
    items.push(...chunk);
    if (!chunk.length || items.length >= total) break;
    page += 1;
  }
  return { items: items.slice(0, count), total };
};

/**
 * One server page. `status` accepts a single value, so a table that shows two
 * statuses loads each and merges them into the column order. Walking one
 * status to the end before the next would keep Completed and Cancelled in
 * separate blocks.
 */
const loadStatusPage = async ({ statuses, q, sort, order, page, size }) => {
  const query = q?.trim() ? q.trim() : undefined;
  if (!statuses.length) return { items: [], total: 0 };
  if (statuses.length === 1) {
    const body = unwrapGet(
      await listAudits({ status: statuses[0], q: query, sort, order, page: page + 1, size })
    );
    return { items: Array.isArray(body?.items) ? body.items : [], total: Number(body?.total) || 0 };
  }
  const start = page * size;
  const prefixes = await Promise.all(
    statuses.map((status) => fetchStatusPrefix({ status, q: query, sort, order, count: start + size }))
  );
  const total = prefixes.reduce((sum, row) => sum + row.total, 0);
  const direction = order === "asc" ? 1 : -1;
  const sortKey = sort || "createdAt";
  const merged = prefixes
    .flatMap((row) => row.items)
    .sort((left, right) => compareMissingLast(listSortValue(left, sortKey), listSortValue(right, sortKey), direction));
  return { items: merged.slice(start, start + size), total };
};

const AuditHub = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const report = useSelector((s) => s.auditHub?.report) || {};
  const [detailsQuery, setDetailsQuery] = useState("");
  const [completedQuery, setCompletedQuery] = useState("");
  const [activeSort, setActiveSort] = useState({ orderBy: "id", order: "desc" });
  const [completedSort, setCompletedSort] = useState({ orderBy: "completedOn", order: "desc" });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [complianceTarget, setComplianceTarget] = useState({
    open: false,
    status: "idle",
    audit: null,
    error: null,
  });
  const [editAuditId, setEditAuditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const currentUserEmail = useMemo(() => readCurrentUserEmail(), []);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [launchTarget, setLaunchTarget] = useState(null);
  const [activityTarget, setActivityTarget] = useState({ open: false, audit: null });
  const [activePage, setActivePage] = useState({ page: 0, size: 10 });
  const [activeList, setActiveList] = useState({ items: [], total: null, status: "idle", error: null });
  const [completedPage, setCompletedPage] = useState({ page: 0, size: 10 });
  const [completedList, setCompletedList] = useState({ items: [], total: null, status: "idle", error: null });
  const [counts, setCounts] = useState({ inProgress: null, approaching: null, stale: null });
  const [reloadToken, setReloadToken] = useState(0);
  const [completedReloadToken, setCompletedReloadToken] = useState(0);
  const debouncedDetails = useDebounced(detailsQuery, 300);
  const debouncedCompleted = useDebounced(completedQuery, 300);
  const openRequest = useRef(0);

  useEffect(() => {
    document.title = "Assessment Hub";
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listAudits({ status: AUDIT_STATUSES.ACTIVE, page: 1, size: 1 }).then(unwrapGet),
      listAudits({
        status: AUDIT_STATUSES.ACTIVE,
        dueWithinDays: APPROACHING_COMPLETION_DAYS,
        page: 1,
        size: 1,
      }).then(unwrapGet),
    ])
      .then(([inProgress, dueSoon]) => {
        if (cancelled) return;
        setCounts({
          inProgress: Number(inProgress?.total) || 0,
          approaching: Number(dueSoon?.total) || 0,
          stale: null,
        });
      })
      .catch(() => {
        if (!cancelled) setCounts((prev) => ({ ...prev, inProgress: null, approaching: null }));
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  useEffect(() => {
    let cancelled = false;
    const sort = sortParams(activeSort);
    setActiveList((prev) => ({ ...prev, status: "loading", error: null }));
    loadStatusPage({
      statuses: statusesFor(selectedStatuses, DETAILS_STATUSES),
      q: debouncedDetails,
      sort: sort.sort,
      order: sort.order,
      page: activePage.page,
      size: activePage.size,
    })
      .then((result) => {
        if (cancelled) return;
        const last = Math.max(0, Math.ceil((result.total || 0) / activePage.size) - 1);
        if (activePage.page > last) {
          setActivePage((prev) => ({ ...prev, page: last }));
          return;
        }
        setActiveList({ items: result.items, total: result.total, status: "succeeded", error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setActiveList({
          items: [],
          total: 0,
          status: "failed",
          error: error?.message || "Unable to load in-progress assessments.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedDetails, activeSort, activePage, selectedStatuses, reloadToken]);

  useEffect(() => {
    let cancelled = false;
    const sort = sortParams(completedSort);
    setCompletedList((prev) => ({ ...prev, status: "loading", error: null }));
    loadStatusPage({
      statuses: statusesFor(selectedStatuses, CLOSED_STATUSES),
      q: debouncedCompleted,
      sort: sort.sort,
      order: sort.order,
      page: completedPage.page,
      size: completedPage.size,
    })
      .then((result) => {
        if (cancelled) return;
        const last = Math.max(0, Math.ceil((result.total || 0) / completedPage.size) - 1);
        if (completedPage.page > last) {
          setCompletedPage((prev) => ({ ...prev, page: last }));
          return;
        }
        setCompletedList({ items: result.items, total: result.total, status: "succeeded", error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setCompletedList({
          items: [],
          total: 0,
          status: "failed",
          error: error?.message || "Unable to load completed assessments.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedCompleted, completedSort, completedPage, selectedStatuses, reloadToken, completedReloadToken]);

  const refreshAudits = () => setReloadToken((n) => n + 1);
  const refreshCompletedAudits = () => setCompletedReloadToken((n) => n + 1);

  useEffect(() => {
    const state = history.location.state;
    const failures = state?.scanDispatchFailures;
    if (Array.isArray(failures) && failures.length) {
      setSnackbar({
        open: true,
        message: `Assessment created. Scan dispatch: ${failures.join(" ")}`,
        severity: "warning",
      });
    } else if (state?.draftSaved) {
      setSnackbar({
        open: true,
        message:
          typeof state.draftSaved === "string" ? `Draft ${state.draftSaved} saved.` : "Draft saved.",
        severity: "success",
      });
    } else {
      return;
    }
    history.replace({ pathname: history.location.pathname, state: undefined });
  }, [history]);

  useEffect(() => {
    if (report.status === "succeeded" && report.downloadUrl) {
      window.open(report.downloadUrl, "_blank", "noopener,noreferrer");
      setSnackbar({ open: true, message: "Report is ready.", severity: "success" });
      dispatch(resetAuditReport());
    } else if (report.status === "failed" && report.error) {
      setSnackbar({ open: true, message: report.error, severity: "error" });
      dispatch(resetAuditReport());
    }
  }, [report.status, report.downloadUrl, report.error, dispatch]);

  const activeLastPage = Math.max(0, Math.ceil((activeList.total || 0) / activePage.size) - 1);
  const activePageIndex = Math.min(activePage.page, activeLastPage);
  const completedLastPage = Math.max(0, Math.ceil((completedList.total || 0) / completedPage.size) - 1);
  const completedPageIndex = Math.min(completedPage.page, completedLastPage);

  const goCreate = () => history.push("/audit-hub/create");
  const openActivity = (audit) => setActivityTarget({ open: true, audit });

  const handleDownload = (audit) => {
    dispatch(requestAuditReport({ auditId: audit.auditId, format: "docx" }));
    setSnackbar({ open: true, message: `Generating report for ${audit.auditCode}…`, severity: "info" });
  };
  const goToFramework = (auditId, account, scope, auditDetail) => {
    const frameworkName = scope?.framework?.name;
    const runId = scope?.latestRunId || "";
    const accountNumber = account?.accountNumber;
    if (!frameworkName || !accountNumber || !auditId) return false;
    localStorage.setItem("selectedAccountNumber", accountNumber);
    if (scope.latestRunEndedAt) {
      localStorage.setItem("selectedScanDate", scope.latestRunEndedAt);
    }
    history.push(
      buildComplianceControlsPath({
        frameworkName,
        runId,
        accountNumber,
        auditId,
      }),
      auditDetail?.auditId === auditId ? { auditDetail } : undefined
    );
    return true;
  };
  const handleOpenAudit = (audit) => {
    const auditId = audit?.auditId;
    if (!auditId) return;
    const requestId = openRequest.current + 1;
    openRequest.current = requestId;
    getAudit(auditId)
      .then((res) => {
        if (openRequest.current !== requestId) return;
        const detail = unwrapGet(res);
        const pairs = [];
        (detail?.accounts || []).forEach((account) => {
          (account.scopes || []).forEach((scope) => pairs.push({ account, scope }));
        });
        const openable = pairs.filter(({ scope }) => scope?.framework?.name);
        // The compliance page switches accounts itself, so only a choice of framework needs asking.
        const frameworks = new Set(openable.map(({ scope }) => frameworkKey(scope.framework)));
        if (frameworks.size === 1) {
          const ready = openable.find(({ scope }) => scope.latestRunId) || openable[0];
          goToFramework(detail.auditId, ready.account, ready.scope, detail);
          return;
        }
        if (!openable.length) {
          setSnackbar({
            open: true,
            message: "This assessment has no framework yet, so its framework view cannot be opened.",
            severity: "info",
          });
          return;
        }
        setComplianceTarget({ open: true, status: "ready", audit: detail, error: null });
      })
      .catch((error) => {
        if (openRequest.current !== requestId) return;
        setSnackbar({
          open: true,
          message: error?.message || "Unable to load this assessment.",
          severity: "error",
        });
      });
  };
  const openComplianceScope = (account, scope) => {
    if (!goToFramework(complianceTarget.audit?.auditId, account, scope, complianceTarget.audit)) return;
    setComplianceTarget((prev) => ({ ...prev, open: false }));
  };

  const listError = activeList.error || completedList.error;
  const filtering = Boolean(detailsQuery.trim() || completedQuery.trim() || selectedStatuses.length);
  const initialLoad = activeList.total == null || completedList.total == null;
  const settled = activeList.status !== "loading" && completedList.status !== "loading";
  const hasAny = (activeList.total || 0) + (completedList.total || 0) > 0;
  const showEmpty = !initialLoad && settled && !filtering && !hasAny && !listError;

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box
            sx={{
              pt: 1.5,
              pb: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              ...auditHubPageSx,
            }}
          >
            <Box sx={tableShellSx}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.125rem", md: "1.25rem" },
                  color: AH_TEXT,
                  letterSpacing: "-0.01em",
                }}
              >
                Assessment Hub
              </Typography>
              <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
                Create and track manual-control assessments.
                {AUDIT_HUB_USE_MOCK
                  ? " Using the local OpenAPI mock until the service is deployed."
                  : ""}
              </Typography>
            </Box>

            <Box sx={tableShellSx}>
              <Box
                sx={{
                  ...toolbarRowSx,
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  overflowX: "visible",
                  mb: 2,
                }}
              >
                <FilterMenu
                  anchorEl={filterAnchor}
                  onOpen={(e) => setFilterAnchor(e.currentTarget)}
                  onClose={() => setFilterAnchor(null)}
                  selectedStatuses={selectedStatuses}
                  onToggleStatus={(status) => {
                    setSelectedStatuses((prev) =>
                      prev.includes(status) ? prev.filter((x) => x !== status) : [...prev, status]
                    );
                    setActivePage((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                    setCompletedPage((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                  }}
                  onClear={() => {
                    setSelectedStatuses([]);
                    setActivePage((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                    setCompletedPage((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                  }}
                />
                <Button
                  startIcon={<AddIcon />}
                  onClick={goCreate}
                  sx={{ ...primaryButtonSx, flexShrink: 0 }}
                >
                  Create New Assessment
                </Button>
              </Box>

              {listError && !hasAny && !filtering && !initialLoad ? (
                <Alert severity="error" sx={{ borderRadius: "10px" }}>
                  {listError}
                </Alert>
              ) : initialLoad && !filtering ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : showEmpty ? (
                <HubEmptyState />
              ) : (
                <>
                  <SummaryCards counts={counts} />
                  <ActiveAuditsTable
                    rows={activeList.items}
                    total={activeList.total || 0}
                    orderBy={activeSort.orderBy}
                    order={activeSort.order}
                    onSort={(field) => {
                      setActiveSort((current) => nextSort(current, field));
                      setActivePage((prev) => ({ ...prev, page: 0 }));
                    }}
                    page={activePageIndex}
                    rowsPerPage={activePage.size}
                    loading={activeList.status === "loading"}
                    error={activeList.error}
                    query={detailsQuery}
                    onQueryChange={(value) => {
                      setDetailsQuery(value);
                      setActivePage((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                    }}
                    emptyMessage={
                      detailsQuery.trim()
                        ? "No assessments match your search."
                        : "No in-progress assessments match the current filters."
                    }
                    onPageChange={(page) => setActivePage((prev) => ({ ...prev, page }))}
                    onRowsPerPageChange={(size) => setActivePage({ page: 0, size })}
                    onOpenAudit={handleOpenAudit}
                    onEdit={(audit) => setEditAuditId(audit.auditId)}
                    onComplete={setCompleteTarget}
                    onCancel={setCancelTarget}
                    onDelete={setDeleteTarget}
                    onActivity={openActivity}
                    onLaunch={setLaunchTarget}
                  />
                  <CompletedAuditsTable
                    rows={completedList.items}
                    total={completedList.total || 0}
                    orderBy={completedSort.orderBy}
                    order={completedSort.order}
                    onSort={(field) => {
                      setCompletedSort((current) => nextSort(current, field));
                      setCompletedPage((prev) => ({ ...prev, page: 0 }));
                    }}
                    page={completedPageIndex}
                    rowsPerPage={completedPage.size}
                    loading={completedList.status === "loading"}
                    error={completedList.error}
                    query={completedQuery}
                    onQueryChange={(value) => {
                      setCompletedQuery(value);
                      setCompletedPage((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                    }}
                    emptyMessage={
                      completedQuery.trim()
                        ? "No assessments match your search."
                        : "No completed assessments match the current filters."
                    }
                    onPageChange={(page) => setCompletedPage((prev) => ({ ...prev, page }))}
                    onRowsPerPageChange={(size) => setCompletedPage({ page: 0, size })}
                    onDownload={handleDownload}
                    onOpenAudit={handleOpenAudit}
                    onActivity={openActivity}
                    onDelete={setDeleteTarget}
                    currentUserEmail={currentUserEmail}
                  />
                </>
              )}
            </Box>
          </Box>
        </Container>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <DeleteAuditDialog
        open={Boolean(deleteTarget)}
        audit={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={(audit) => {
          setDeleteTarget(null);
          if (CLOSED_STATUSES.includes(audit?.status)) refreshCompletedAudits();
          else refreshAudits();
          setSnackbar({ open: true, message: `${audit?.auditCode || "Assessment"} deleted.`, severity: "success" });
        }}
      />
      <CompleteAuditDialog
        open={Boolean(completeTarget)}
        audit={completeTarget}
        onClose={() => setCompleteTarget(null)}
        onCompleted={(audit) => {
          setCompleteTarget(null);
          refreshAudits();
          setSnackbar({ open: true, message: `${audit.auditCode || "Assessment"} completed.`, severity: "success" });
        }}
      />
      <CancelAuditDialog
        open={Boolean(cancelTarget)}
        audit={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={(audit) => {
          setCancelTarget(null);
          refreshAudits();
          setSnackbar({ open: true, message: `${audit.auditCode || "Assessment"} cancelled.`, severity: "success" });
        }}
      />
      <LaunchAuditDialog
        open={Boolean(launchTarget)}
        audit={launchTarget}
        onClose={() => setLaunchTarget(null)}
        onLaunched={(audit, failures) => {
          setLaunchTarget(null);
          refreshAudits();
          const code = audit.auditCode || "Assessment";
          setSnackbar(
            failures.length
              ? { open: true, message: `${code} launched. Scan dispatch: ${failures.join(" ")}`, severity: "warning" }
              : { open: true, message: `${code} launched.`, severity: "success" }
          );
        }}
      />
      <EditAuditDialog
        open={Boolean(editAuditId)}
        auditId={editAuditId}
        onClose={() => setEditAuditId(null)}
        onRefresh={refreshAudits}
        onSaved={() => {
          setEditAuditId(null);
          refreshAudits();
        }}
        onLaunched={(audit, failures) => {
          setEditAuditId(null);
          refreshAudits();
          const code = audit?.auditCode || "Assessment";
          setSnackbar(
            failures?.length
              ? { open: true, message: `${code} launched. Scan dispatch: ${failures.join(" ")}`, severity: "warning" }
              : { open: true, message: `${code} launched.`, severity: "success" }
          );
        }}
      />
      <AuditComplianceDialog
        open={complianceTarget.open}
        status={complianceTarget.status}
        audit={complianceTarget.audit}
        error={complianceTarget.error}
        onClose={() => setComplianceTarget((prev) => ({ ...prev, open: false }))}
        onOpenScope={openComplianceScope}
      />
      <ActivityTrailDialog
        open={activityTarget.open}
        audit={activityTarget.audit}
        onClose={() => setActivityTarget((prev) => ({ ...prev, open: false }))}
      />
    </AuditHubThemeProvider>
  );
};

export default AuditHub;
